import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { TranslationComponent } from '../TranslationComponent';
import { EPtwStatus, IPtw } from '@relab/alstom-ptw-shared';
import { ptwEditAction, resetServerRequestAction } from '../../redux/actions';
import { connect } from 'react-redux';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appStateServerRequests } from '../../redux/selectors';
import { useTranslation } from 'react-i18next';
import { Button as FormikButton, Form, TextArea } from 'formik-semantic-ui';
import { client } from '../../redux/store';

type Props = {
  ptw: IPtw;
  detailScreen?: boolean;
  onDelete?: VoidFunction;
  ptwEditAction: typeof ptwEditAction;
  resetServerRequestAction: typeof resetServerRequestAction;
  editPtwServerRequest?: ServerRequestStatus;
  deletePtwServerRequest?: ServerRequestStatus;
};

const PPtwDeleteModal: FC<Props> = ({
  ptw,
  detailScreen,
  onDelete,
  ptwEditAction,
  editPtwServerRequest,
  deletePtwServerRequest,
  resetServerRequestAction,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSetPtwOnDeleted = useCallback(
    async (data: { deletionNotes: string; deletionAttachment?: File }) => {
      if (data.deletionAttachment) {
        const formData = new FormData();
        formData.append('deletionAttachment', data.deletionAttachment);

        await client.post(`ptw/${ptw._id}/file`, formData);
      }

      ptwEditAction({ ...ptw, status: EPtwStatus.CANCELLATO, deletionNotes: data.deletionNotes });
    },
    [ptw, ptwEditAction]
  );

  useEffect(() => {
    if (open) {
      if (editPtwServerRequest?.success || deletePtwServerRequest?.success) {
        onDelete?.();
        setOpen(false);
        resetServerRequestAction(ActionType.PTW_EDIT);
      } else if (editPtwServerRequest?.error || deletePtwServerRequest?.error) {
        setError(editPtwServerRequest?.error || deletePtwServerRequest?.error);
      }
    }
  }, [editPtwServerRequest, deletePtwServerRequest, onDelete, open, resetServerRequestAction]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      trigger={
        <Button
          icon={!detailScreen}
          primary={detailScreen}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
        >
          {detailScreen ? <TranslationComponent stringKey={'Delete Ptw'} /> : <Icon name={'trash alternate'} />}
        </Button>
      }
    >
      <Modal.Header>
        <TranslationComponent stringKey={'Delete Ptw'} />
      </Modal.Header>

      <Form
        initialValues={{ deletionNotes: '' }}
        onSubmit={handleSetPtwOnDeleted}
        render={({ setFieldValue }) => (
          <>
            <Modal.Content>
              <div style={{ padding: '1.5rem' }}>
                <TranslationComponent stringKey={`${'You are about to set this Ptw on deleted status'}`} />
                {!!error ? (
                  error
                ) : (
                  <>
                    <TextArea label={t('Deletion notes')} name="deletionNotes" />

                    <input
                      type="file"
                      name="deletionAttachment"
                      onChange={(e) => {
                        setFieldValue('deletionAttachment', e.target.files[0]);
                      }}
                    />
                  </>
                )}
              </div>
            </Modal.Content>
            <Modal.Actions>
              <div style={{ padding: '1.5rem' }}>
                <Button onClick={() => setOpen(false)}>
                  <TranslationComponent stringKey={'Cancel'} />
                </Button>
                <FormikButton.Submit>
                  <TranslationComponent stringKey={'Delete'} />
                </FormikButton.Submit>
              </div>
            </Modal.Actions>
          </>
        )}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  editPtwServerRequest: appStateServerRequests(state, ActionType.PTW_EDIT),
  deletePtwServerRequest: appStateServerRequests(state, ActionType.PTW_DELETE),
});

export const PtwDeleteModal = connect(mapStateToProps, { ptwEditAction, resetServerRequestAction })(PPtwDeleteModal);
